import { useEffect, useState } from "react";
import {
  Link,
  redirect,
  useActionData,
  useFetcher,
  useNavigation,
  useParams,
  useSearchParams,
} from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery, useSuspenseQueries } from "@tanstack/react-query";
import type { EventOptions, RecordResultsRequest, RequestBody } from "@repo/shared-types";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage } from "@/lib/session";
import { boundsOfEvent, formOf, partLabel, readPartChanges, readParts, type PartBounds } from "@/lib/event-form";
import { loadSpaceContext, statusLabel, useSpaceContext } from "@/lib/events";
import {
  OVERALL,
  canShareInDivision,
  divisionLabel,
  eventParts,
  hasOverall,
  isRanked,
  isScored,
  levelLabel,
  oneLevel,
  ordinal,
} from "@/lib/results";
import { tenantPath } from "@/lib/tenants";
import { formatMoment, viewerZone } from "@/lib/times";
import { ErrorNote } from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import { AddPartButton, PartSheet, PartSummary } from "@/components/event-fields";
import { useClosedWhenDone, type Outcome } from "@/components/modal";
import { ShareStep } from "@/components/part-dialog";
import {
  LevelStep,
  PartResult,
  RecordBar,
  WorkoutPartDialog,
  WorkoutSheet,
  useSendWorkout,
  useWorkout,
  type ShareAnswer,
} from "@/components/workout";
import { boardPath, readStandings, useStandings, type Standing } from "@/lib/event-boards";
import { teamHoldingLevel, teamsOn } from "@/lib/teams";
import { partTone, type PartTone } from "@/lib/part-tone";
import { Conversation } from "@/components/conversation";
import { looksLikeAPicture, uploadPicture } from "@/lib/images";
import { Avatar } from "@/components/avatar";
import { EventBanner } from "@/components/event-image";
import { EventMenu, PartMenu } from "@/components/event-menus";
import { BackLink, TopBar } from "@/components/navigation";
import { FollowControl } from "@/components/follow";
import { ChevronRight, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageTitle } from "@/components/brand";

/**
 * One event: when it is, what it is, its parts with how each is scored, your
 * result for it, and its leaderboards.
 *
 * Every member records a result here, a part at a time: each part's card
 * says where their result stands on its board and links to that board's own
 * screen (routes/board.tsx, since 10 October). The page is read first; the
 * bar at the bottom starts recording (decided with Tiago 13 October), and
 * then each card has a button opening a sheet for its part — its score,
 * level, Rx+, feel and note — whose Save keeps it on the page, marked as not
 * sent, until the bar's Register workout (then Save changes) sends it all,
 * with the photo and, in an event ranked overall, the one level asked as
 * recording starts. A first result that would be private asks whether to
 * share it, notes, feel and photo included or not; a result recorded here
 * carries when it was registered. They share, unshare or delete it
 * here too. An event ranked overall says above its parts where the viewer
 * stands overall, linking to the ranking's own screen.
 *
 * The owner and admins holding manage_events also edit and delete the event,
 * and add, edit and delete its parts. A part's title, description, start and
 * deadline can change; how it is scored cannot — there is no control for it,
 * and the api has no field for it either. The owner and admins holding
 * moderate hide a result on one part's board, and are linked to the event's
 * hidden results to put one back; those holding verify_scores verify them,
 * and are linked to the queue of those waiting.
 *
 * A member opening a draft gets the same 404 as a missing event, from the api.
 */

export async function eventLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const [context, event, entry] = await Promise.all([
    loadSpaceContext(request, slug),
    read(queries.event(slug, eventId)),
    read(queries.myEntry(slug, eventId)),
  ]);
  // An inbox line names a part the way the page did when boards were on it,
  // ?part= (and ?level=&division=): that board now has its own screen, which
  // reads those keys as its own.
  const url = new URL(request.url);
  const linked = url.searchParams.get("part");
  const target =
    linked === OVERALL
      ? hasOverall(event) && OVERALL
      : event.parts.find((part) => part.id === linked && isRanked(part))?.id;
  if (target) throw redirect(`${boardPath(`/t/${slug}/events/${eventId}`, target)}${url.search}`);

  await Promise.all([
    read(queries.eventComments(slug, eventId)),
    context.canManageEvents ? read(queries.eventOptions()) : null,
    readStandings(slug, event, entry, context.profile.division),
    // One item is enough: the count comes with every page.
    context.canVerify && event.parts.length > 0 ? read(queries.queue(slug, eventId, { limit: 1 })) : null,
    context.canModerate && event.parts.length > 0 ? read(queries.hidden(slug, eventId, { limit: 1 })) : null,
    event.parts.some((part) => part.isTeam) ? read(queries.myTeams(slug, eventId)) : null,
  ]);
  return null;
}

/**
 * What the page says back. A change the page would not show where it was
 * made — a workout's changes saved, sharing, a part edited
 * or deleted from its cog, a photo tried again, a deletion, and a board
 * line's verifying and hiding — is said by a toast from here (decided with
 * Tiago 12 October). `ok` stays for what shows anyway: a first result
 * registered in its cards, a part added to the list.
 */
type ActionResult = {
  error?: string;
  /** The box the error is about, in a form that has one. */
  field?: string;
  ok?: string;
  /** The workout was sent: what the page typed is now what it reads. */
  saved?: boolean;
  /** It was, and the photo chosen with it did not go up. */
  photoFailed?: boolean;
  photoAdded?: boolean;
};

export async function eventAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const events = api.space(slug).events;

  try {
    switch (String(form.get("intent") ?? "")) {
      // The whole workout, put together on the page from what each part's
      // sheet read while recording (EventPage's send): one request, so a
      // retry changes what it made. A photo chosen before registering goes
      // up after it.
      case "record": {
        let body: RequestBody<RecordResultsRequest>;
        try {
          body = JSON.parse(String(form.get("body") ?? ""));
        } catch {
          return { error: "Your result could not be read. Try again." };
        }
        const saved = await events.entry.record(eventId, body);
        const first = form.get("first") !== null;
        const done = first ? "Registered" : "Saved";
        const file = form.get("file");
        if (form.get("removePhoto") !== null) await events.entry.photo.remove(eventId);
        if (looksLikeAPicture(file)) {
          try {
            await uploadPicture({
              start: () => events.entry.photo.start(eventId),
              confirm: (mediaId) => api.space(slug).media.uploaded(mediaId),
              file,
            });
          } catch (error) {
            return { saved: true, photoFailed: true, error: `${done}, but the photo did not go up: ${errorMessage(error, "try it again")}.` };
          }
        }
        // A first result shows itself in its parts' cards, so its line stays
        // on the page; a part saved since is said by a toast.
        const said = (ok: string): ActionResult => {
          if (first) return { saved: true, ok };
          toast(ok);
          return { saved: true };
        };
        if (saved.shared) return said(`${done}, and on the leaderboard.`);
        const profile = await api.me.profile.get();
        if (!canShareInDivision(saved.parts, profile.division)) {
          return said(`${done}, privately. Set your division in Account to show it on a leaderboard.`);
        }
        return said(`${done}. Only you can see it, in your log.`);
      }

      case "share":
        await events.entry.share(eventId);
        toast("Your result is on the leaderboard.");
        return {};

      // Everything comes off — notes, feel and photo too — so sharing again
      // asks afresh what goes with it (decided with Tiago 13 October).
      case "unshare": {
        const entryId = String(form.get("entry") ?? "");
        await api.entries.unshare(entryId);
        await api.entries.update(entryId, { detailsShared: false });
        toast("Taken off the leaderboard. It stays in your log.");
        return {};
      }

      // Gone from the page is easy to miss: what goes is said.
      case "delete-entry":
        await api.entries.delete(String(form.get("entry") ?? ""));
        toast("Your result is deleted.");
        return {};

      // Your result's photo (7g): shrunk here, posted to the store, confirmed.
      case "photo": {
        const file = form.get("file");
        if (!looksLikeAPicture(file)) return { error: "Choose a picture to add." };
        // One that does not go up stays on the page to be tried again.
        try {
          await uploadPicture({
            start: () => events.entry.photo.start(eventId),
            confirm: (mediaId) => api.space(slug).media.uploaded(mediaId),
            file,
          });
        } catch (error) {
          return { photoFailed: true, error: `Your photo did not go up: ${errorMessage(error, "try it again")}.` };
        }
        toast("Photo added. It shows for 24 hours from your first.");
        return { photoAdded: true };
      }

      case "remove-photo":
        await events.entry.photo.remove(eventId);
        toast("Photo taken down.");
        return {};

      // From a board's line (routes/board.tsx): an athlete's result by its
      // part and entry, or a team by its id. Each answers with an object, even
      // an empty one: the sheet asking first closes on an answer without an
      // error, and the toast says the rest.
      case "hide":
        if (form.get("team")) await events.teams.hide(eventId, String(form.get("team")));
        else await events.hide(eventId, String(form.get("part") ?? ""), String(form.get("entry") ?? ""));
        toast("Hidden.");
        return {};

      case "verify": {
        const changedAt = String(form.get("changedAt") ?? "");
        if (form.get("team")) await events.teams.verify(eventId, String(form.get("team")), { changedAt });
        else await events.verify(eventId, String(form.get("part") ?? ""), String(form.get("entry") ?? ""), { changedAt });
        toast("Verified.");
        return {};
      }

      case "unverify":
        if (form.get("team")) await events.teams.unverify(eventId, String(form.get("team")));
        else await events.unverify(eventId, String(form.get("part") ?? ""), String(form.get("entry") ?? ""));
        toast("Verification taken back.");
        return {};

      // Said before going: the space's feed shows it only by its absence.
      case "delete-event":
        await events.delete(eventId);
        toast("Event deleted.");
        return redirect(`/t/${slug}`);

      // From its sheet, which checked it already and closes once it is
      // added: the card it becomes shows where, the toast that it worked.
      case "add-part": {
        const [profile, options] = await Promise.all([api.me.profile.get(), api.eventOptions()]);
        const read = readParts(form, viewerZone(profile.timeZone).zone, options, Number(form.get("position")) || 0);
        if (read.error !== undefined) return { error: read.error, field: read.field };
        const [part] = read.value;
        if (!part) return { error: "Describe the part to add." };
        await events.parts.create(eventId, part);
        toast("Part added.");
        return {};
      }

      case "update-part": {
        const profile = await api.me.profile.get();
        const read = readPartChanges(form, viewerZone(profile.timeZone).zone);
        if (read.error !== undefined) return { error: read.error, field: read.field };
        await events.parts.update(eventId, String(form.get("part") ?? ""), read.value);
        toast("Part updated.");
        return {};
      }

      case "delete-part":
        await events.parts.delete(eventId, String(form.get("part") ?? ""));
        toast("Part deleted.");
        return {};

      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

/**
 * A part card's outline, by how the athlete's result stands (0070). The
 * card's own ring has a dark colour of its own, so each says its dark too.
 */
const OUTLINES: Record<PartTone | "none", string> = {
  ok: "ring-2 ring-success dark:ring-success",
  waiting: "ring-2 ring-warning dark:ring-warning",
  bad: "ring-2 ring-danger dark:ring-danger",
  none: "",
};

export default function EventPage() {
  const params = useParams();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const { session, tenant, canManageEvents, canVerify, canModerate, profile, zone } = useSpaceContext(slug);
  const [{ data: event }, { data: entry }, { data: thread }] = useSuspenseQueries({
    queries: [queries.event(slug, eventId), queries.myEntry(slug, eventId), queries.eventComments(slug, eventId)],
  });
  // What the loader reads only for those who may see it, or when the event has it.
  const hasParts = event.parts.length > 0;
  const options = useQuery({ ...queries.eventOptions(), enabled: canManageEvents }).data ?? null;
  const waiting =
    useQuery({ ...queries.queue(slug, eventId, { limit: 1 }), enabled: canVerify && hasParts }).data?.waiting ?? null;
  const hidden =
    useQuery({ ...queries.hidden(slug, eventId, { limit: 1 }), enabled: canModerate && hasParts }).data?.hidden ??
    null;
  const teams =
    useQuery({ ...queries.myTeams(slug, eventId), enabled: event.parts.some((part) => part.isTeam) }).data?.teams ??
    [];
  const { standings, overall } = useStandings(slug, event, entry, profile.division);
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";
  const base = tenantPath(tenant);
  const eventPath = `${base}/events/${event.id}`;
  const recordable = eventParts(event, entry);
  const records = recordable.some(isScored);
  const single = oneLevel(event);
  // In an event ranked overall, a team the viewer is on holds their level (0037).
  const holding = single ? teamHoldingLevel(teams) : null;
  const heldOn = holding ? event.parts.findIndex((part) => part.id === holding.partId) : -1;
  const levelHeldBy =
    holding?.level && heldOn >= 0 ? { part: `${partLabel(heldOn)}. ${event.parts[heldOn]!.title}`, level: holding.level } : null;
  // What each part's sheet read while recording, kept until the bar sends
  // it: one request for every part, so sharing — the whole result's — is
  // asked once (decided with Tiago 13 October).
  const workout = useWorkout({ savedLevel: entry?.level ?? null, registered: entry !== null, single, outcome: result });
  const post = useSendWorkout(workout);
  // Every board is men's or women's: without a division it stays private.
  const shareable = canShareInDivision(entry?.parts ?? event.parts, profile.division);

  /**
   * Sends the workout: every part typed, its one level in an event ranked
   * overall, and the answer to sharing. When it was done is never sent from
   * here: a result recorded in a space is stamped by the api with when it
   * was registered (decided with Tiago 13 October).
   */
  const send = (answer: ShareAnswer | null) => {
    const list = Object.values(workout.drafts);
    const body: RequestBody<RecordResultsRequest> = {
      ...(single ? { level: workout.level } : {}),
      ...(answer?.share ? { share: true, detailsShared: answer.detailsShared } : {}),
      parts: list.map((draft) => ({
        partId: draft.part.id,
        level: draft.part.level,
        // The badge rides on Rx alone, and the event's one level is asked as recording starts.
        rxPlus: single && draft.part.rxPlus !== null && workout.level !== "rx" ? false : draft.part.rxPlus,
        scores: draft.part.scores,
        team: draft.part.team,
      })),
      notes: list.flatMap((draft) => (draft.note ? [{ partId: draft.note.id, note: draft.note.note, feel: draft.note.feel }] : [])),
    };
    post(body, entry ? {} : { first: "1" });
  };
  /** A first result asks whether to share it, while it would be private; later sends just save. */
  const register = () => (!entry && shareable ? workout.setAsking(true) : send(null));
  const openPart = recordable.find((part) => part.id === workout.open);
  // An inbox line naming a part without a board — the rest go to theirs, by
  // the loader — opens at that part's card.
  const [search] = useSearchParams();
  const linked = search.get("part");
  useEffect(() => {
    if (linked) document.getElementById(`part-${linked}`)?.scrollIntoView?.({ block: "start" });
  }, [linked]);

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        {/* The way back, following and the event's cog share the top, as a phone app's bar. */}
        <TopBar>
          <BackLink to={base}>{tenant.name}</BackLink>
          <div className="flex shrink-0 items-center gap-2">
            <FollowControl event={event} base={base} />
            {canManageEvents && <EventMenu editTo={`${eventPath}/edit`} />}
          </div>
        </TopBar>
        {event.image?.url && <EventBanner image={event.image} />}
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary">{event.formatLabel}</Badge>
          {event.sportLabel && <Badge variant="secondary">{event.sportLabel}</Badge>}
          {event.status !== "published" && (
            <Badge className="bg-warning-muted text-warning">{statusLabel(event.status)}</Badge>
          )}
        </div>
        <PageTitle of={[event.title]} />
        <h1 className="page-title">{event.title}</h1>
        {/* A post has no dates of its own (0090): its card says when it went out. */}
        {event.isPost ? (
          event.createdBy && (
            <p className="text-muted-foreground">
              {"Posted by "}
              <Avatar person={event.createdBy} size="xs" className="mr-1" />@{event.createdBy.username}
            </p>
          )
        ) : (
          <p className="text-muted-foreground">
            <Clock aria-hidden="true" size={16} className="mr-1.5 inline-block align-[-2px]" />
            {formatMoment(event.startsAt, zone)}
            {event.endsAt && ` – ${formatMoment(event.endsAt, zone)}`}
            {event.createdBy && (
              <>
                {" · posted by "}
                <Avatar person={event.createdBy} size="xs" className="mr-1" />@{event.createdBy.username}
              </>
            )}
          </p>
        )}
      </div>

      {event.status !== "published" && (
        <Alert role="note" className="border-transparent bg-warning-muted text-warning">
          <AlertDescription className="text-warning">
            {statusLabel(event.status)}
            {event.status === "scheduled" && event.publishedAt
              ? ` — members will see it from ${formatMoment(event.publishedAt, zone)}.`
              : " — only the owner and admins with Manage events can see it."}
          </AlertDescription>
        </Alert>
      )}

      {result?.error && <ErrorNote message={result.error} />}
      {result?.ok && (
        <Alert role="status" className="border-transparent bg-success-muted text-success">
          <AlertDescription className="text-success">{result.ok}</AlertDescription>
        </Alert>
      )}

      {event.description && (
        <Card size="sm">
          <CardContent className="whitespace-pre-line text-base">{event.description}</CardContent>
        </Card>
      )}

      {overall && <OverallCard standing={overall} to={boardPath(eventPath, OVERALL)} />}

      {(waiting !== null || hidden !== null) && (
        <div className="flex flex-wrap gap-2">
          {/* Each a button with its count inside: the whole of it opens the page. */}
          {waiting !== null && (
            <Button asChild variant="outline" size="lg">
              <Link to={`${eventPath}/verify`}>
                Verify results <span className="text-muted-foreground">({waiting} waiting)</span>
              </Link>
            </Button>
          )}
          {hidden !== null && (
            <Button asChild variant="outline" size="lg">
              <Link to={`${eventPath}/hidden`}>
                Hidden results <span className="text-muted-foreground">({hidden} hidden)</span>
              </Link>
            </Button>
          )}
        </div>
      )}

      {/* A post has no parts, and nothing to add one with (0090). */}
      {!event.isPost && (
        <section className="space-y-3" aria-label="Parts">
          <h2 className="section-label">Parts</h2>
          {event.parts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No parts yet.</p>
          ) : (
            <ul className="space-y-4">
              {event.parts.map((part, index) => (
                <li key={part.id} id={`part-${part.id}`} className="scroll-mt-4">
                  {/* A card with a board ends with the way to it, flush along its foot. */}
                  <Card
                    className={cn(
                      "gap-4",
                      standings[part.id] && "pb-0",
                      // eventParts maps the event's parts one to one.
                      OUTLINES[partTone(recordable[index]!, teamsOn(teams, part.id)) ?? "none"],
                    )}
                  >
                    <CardHeader>
                      <PartSummary part={part} index={index} zone={zone} />
                      {canManageEvents && (
                        <CardAction>
                          <PartMenu part={part} index={index} zone={zone} bounds={boundsOfEvent(event, zone)} />
                        </CardAction>
                      )}
                    </CardHeader>
                    {records && recordable[index] && (
                      // Gone while the part shows no result, so the card keeps one gap.
                      <CardContent className="empty:hidden">
                        <PartResult
                          part={recordable[index]}
                          draft={workout.drafts[part.id]}
                          registered={entry !== null}
                          recording={workout.recording}
                          showLevel={!entry?.level && !single}
                          spaceName={tenant.name}
                          teams={{ teams: teamsOn(teams, part.id), base, eventId: event.id }}
                          place={standings[part.id]?.place ?? null}
                          onOpen={() => workout.setOpen(part.id)}
                          onUndo={() => workout.setDraft(part.id, undefined)}
                        />
                      </CardContent>
                    )}
                    {standings[part.id] && (
                      <BoardLink
                        to={boardPath(eventPath, part.id)}
                        label={`Part ${partLabel(index)} leaderboard`}
                        results={standings[part.id]!.results}
                        teams={part.isTeam}
                      />
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
          {/* Right after the parts, above everything about results. */}
          {canManageEvents && options && <AddPart options={options} index={event.parts.length} zone={zone} bounds={boundsOfEvent(event, zone)} />}
        </section>
      )}

      {records && workout.sheet && (
        <WorkoutSheet
          entry={entry}
          workout={workout}
          zone={zone}
          single={single}
          shareable={shareable}
          noDivision={!shareable}
          profileDivision={profile.division}
          deadlinePassed={event.parts.some((part) => part.hasLeaderboard && part.deadlinePassed)}
          spaceName={tenant.name}
          photoFailed={Boolean(result?.photoFailed)}
          busy={busy}
          images={session.images}
          logLink
          onShare={() => workout.setAsking(true)}
        />
      )}

      {openPart && (
        <WorkoutPartDialog
          // A fresh dialog each time: it starts from the part as it stands.
          key={openPart.id}
          part={openPart}
          index={recordable.indexOf(openPart)}
          workout={workout}
          single={single}
          teamSlug={tenant.slug}
        />
      )}

      {workout.askingLevel && (
        <LevelStep
          eventTitle={event.title}
          level={workout.level}
          registered={entry !== null}
          levelHeldBy={levelHeldBy}
          onChoose={(level) => {
            workout.setLevel(level);
            workout.setAskingLevel(false);
            workout.setRecording(true);
          }}
          onClose={() => workout.setAskingLevel(false)}
        />
      )}

      {workout.asking && (
        <ShareStep
          spaceName={tenant.name}
          busy={busy}
          onAnswer={send}
          onClose={() => workout.setAsking(false)}
        />
      )}

      <Conversation
        key={event.id}
        target={{ slug: tenant.slug, eventId: event.id, entryId: null }}
        page={thread}
        reactions={event.reactions ?? { counts: [], mine: null }}
        writes
        closed={event.status === "published" ? null : "Comments and reactions open when the event is published."}
      />

      {records && <RecordBar workout={workout} entry={entry} busy={busy} onSend={register} />}
    </section>
  );
}

/**
 * A part's card ends with the way to its board, which says how many are on
 * it — teams on a part done in teams: a row across the card's foot.
 */
function BoardLink({ to, label, results, teams }: { to: string; label: string; results: number; teams: boolean }) {
  const noun = teams ? (results === 1 ? "team" : "teams") : results === 1 ? "result" : "results";
  return (
    <Link
      to={to}
      aria-label={`${label}, ${results} ${noun}`}
      className="flex min-h-12 items-center justify-between border-t border-border px-(--card-spacing) font-medium outline-none transition-colors hover:bg-muted focus-visible:bg-muted"
    >
      Leaderboard
      <span className="flex items-center gap-1 text-sm font-normal text-muted-foreground">
        {results} {noun}
        <ChevronRight aria-hidden="true" className="size-4" />
      </span>
    </Link>
  );
}

/**
 * Where the viewer stands overall, above the parts: their place on their own
 * board — filled, the event's headline for them — or, with none, the way to
 * the ranking.
 */
function OverallCard({ standing, to }: { standing: Standing; to: string }) {
  const { place, choice } = standing;
  const board = [choice.level ? levelLabel(choice.level) : "", divisionLabel(choice.division)].filter(Boolean).join(" · ");
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-4 rounded-4xl px-6 py-4 shadow-md outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
        place
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "bg-card text-card-foreground ring-1 ring-foreground/5 hover:bg-muted dark:ring-foreground/10",
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <p className={cn("text-sm font-medium", place ? "opacity-80" : "")}>Overall{place ? ` · ${board}` : ""}</p>
        {place ? (
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-5xl font-semibold leading-none tracking-tight tabular-nums">{ordinal(place.rank)}</span>
            <span className="font-medium">
              of {place.of}
              {standing.score && ` · ${standing.score}`}
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {standing.results === 0 ? "Nobody on the ranking yet" : `${standing.results} on the ranking`}
          </p>
        )}
      </div>
      <ChevronRight aria-hidden="true" className="size-6" />
    </Link>
  );
}

/**
 * Add a part: its sheet, as on the New event form (decided with Tiago 20
 * October), sent as soon as Add part is pressed — by fetcher, so a refusal
 * is said in the sheet and an added part closes it.
 */
function AddPart({
  options,
  index,
  zone,
  bounds,
}: {
  options: EventOptions;
  index: number;
  zone: string;
  /** The event's dates: a new part starts due on its deadline (0083). */
  bounds: PartBounds;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AddPartButton onClick={() => setOpen(true)} />
      {open && (
        <NewPartSheet options={options} index={index} zone={zone} bounds={bounds} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/** The sheet itself, drawn afresh each time it opens, with a fetcher of its own. */
function NewPartSheet({
  options,
  index,
  zone,
  bounds,
  onClose,
}: {
  options: EventOptions;
  index: number;
  zone: string;
  bounds: PartBounds;
  onClose: () => void;
}) {
  const fetcher = useFetcher<Outcome>();
  useClosedWhenDone(fetcher, onClose);
  return (
    <PartSheet
      title={`New part ${partLabel(index)}`}
      submit="Add part"
      options={options}
      index={index}
      check={(form) => readParts(form, zone, options, index)}
      onSave={(fields) => {
        const form = formOf(fields);
        form.set("intent", "add-part");
        form.set("position", String(index));
        void fetcher.submit(form, { method: "post" });
      }}
      onClose={onClose}
      busy={fetcher.state !== "idle"}
      sent={fetcher.data}
      bounds={bounds}
    />
  );
}
